import {
  Children,
  Fragment,
  isValidElement,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { ChevronDown } from 'lucide-react';
import { Card } from '@/shared/ui/core';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/shared/ui/core/Collapsible';
import { cn } from '@/shared/lib/cn';

export type DetailSectionCardProps = {
  title: ReactNode;
  description?: string;
  /** Leading icon beside title (Field / MES panels). */
  icon?: ReactNode;
  children: ReactNode;
  className?: string;
  /** Extra controls in the title row (optional). */
  headerEnd?: ReactNode;
  dense?: boolean;
  /** Draw a rule under the title row (separates header from body). */
  headerDivider?: boolean;
  /** MX2 Field EXEC — progressive disclosure. */
  collapsible?: boolean;
  /** When `collapsible`, initial open state (default true). */
  defaultOpen?: boolean;
};

/**
 * Titled information group for WEB detail pages.
 * Breadcrumb carries the page title — section cards only group body content.
 * Optional `collapsible` for Field EXEC secondary panels (MX2).
 */
export function DetailSectionCard({
  title,
  description,
  icon,
  children,
  className,
  headerEnd,
  dense = false,
  headerDivider = false,
  collapsible = false,
  defaultOpen = true,
}: DetailSectionCardProps) {
  const [open, setOpen] = useState(defaultOpen);

  const titleBlock = (
    <div className="min-w-0 flex-1 text-left">
      <h3 className="text-h3 m-0 flex items-center gap-2">
        {icon ? (
          <span className="inline-flex shrink-0 text-primary" aria-hidden>
            {icon}
          </span>
        ) : null}
        <span className="min-w-0 font-bold leading-tight">{title}</span>
      </h3>
      {description ? (
        <p className="m-0 mt-1 text-caption text-text-secondary">{description}</p>
      ) : null}
    </div>
  );

  if (!collapsible) {
    return (
      <Card dense={dense} className={cn('space-y-3 p-4', className)}>
        <div
          className={cn(
            'flex items-center justify-between gap-3',
            headerDivider && 'border-b border-border/60 pb-2.5',
          )}
        >
          {titleBlock}
          {headerEnd ? (
            <div className="shrink-0">{headerEnd}</div>
          ) : null}
        </div>
        {children}
      </Card>
    );
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <Card dense={dense} className={cn('space-y-0', className)}>
        <div className="flex items-start gap-2">
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className={cn(
                'flex min-h-[var(--touch-target-min)] min-w-0 flex-1 items-center gap-2 rounded-control',
                'text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-focus-ring)]',
              )}
            >
              {titleBlock}
              <ChevronDown
                className={cn(
                  'size-5 shrink-0 text-text-muted transition-transform',
                  open && 'rotate-180',
                )}
                aria-hidden
              />
            </button>
          </CollapsibleTrigger>
          {headerEnd ? <div className="shrink-0 pt-1">{headerEnd}</div> : null}
        </div>
        <CollapsibleContent className="space-y-3 pt-3 data-[state=closed]:hidden">
          {children}
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}

export type DetailFieldProps = {
  label: string;
  children: ReactNode;
  mono?: boolean;
  className?: string;
  /** Occupy full row on desktop (all columns). Prefer over `sm:col-span-*`. */
  fullWidth?: boolean;
};

/** Label + value cell for read-only detail grids. */
export function DetailField({
  label,
  children,
  mono = false,
  className,
  fullWidth = false,
}: DetailFieldProps) {
  return (
    <div className={cn('min-w-0', className)}>
      <p className="text-label m-0 text-text">{label}</p>
      <div
        className={cn(
          'mt-0.5 text-body text-text',
          fullWidth
            ? 'break-words whitespace-normal'
            : 'overflow-x-auto whitespace-nowrap',
          mono && 'font-mono text-caption tabular-nums',
        )}
      >
        {children}
      </div>
    </div>
  );
}

/** Desktop column count for `DetailFieldGrid`. Mobile (`< sm`) always stacks to 1 column. */
export type DetailFieldGridColumns = 1 | 2 | 3;

export type DetailFieldGridProps = {
  children: ReactNode;
  className?: string;
  /**
   * Columns on `sm+` (default 2). Mobile always 1 column.
   * Use 1 for sparse/long fields; 3 only when labels are short and density is needed.
   */
  columns?: DetailFieldGridColumns;
};

function isFullWidthField(child: ReactElement): boolean {
  const props = child.props as DetailFieldProps;
  if (props.fullWidth) return true;
  if (
    typeof props.className === 'string' &&
    /\b(?:sm:)?col-span-(?:2|3|full)\b/.test(props.className)
  ) {
    return true;
  }
  return false;
}

const DESKTOP_COLS: Record<DetailFieldGridColumns, string> = {
  1: 'sm:grid-cols-1',
  2: 'sm:grid-cols-2',
  3: 'sm:grid-cols-3',
};

/** `Children.toArray` giữ fragment thành một phần tử, nên lưới coi cả cụm là một ô. */
function flattenFields(nodes: ReactNode[]): ReactNode[] {
  const flat: ReactNode[] = [];
  for (const node of nodes) {
    if (node == null || node === false || node === true) continue;
    if (isValidElement(node) && node.type === Fragment) {
      const nested = (node.props as { children?: ReactNode }).children;
      flat.push(...flattenFields(Children.toArray(nested)));
      continue;
    }
    flat.push(node);
  }
  return flat;
}

function chunkFields(
  items: ReactNode[],
  columns: DetailFieldGridColumns,
): ReactNode[][] {
  if (columns <= 1) {
    return items.map((child) => [child]);
  }

  const rows: ReactNode[][] = [];
  let buf: ReactNode[] = [];

  const flush = () => {
    if (buf.length === 0) return;
    rows.push(buf);
    buf = [];
  };

  for (const child of items) {
    if (isValidElement(child) && isFullWidthField(child)) {
      flush();
      rows.push([child]);
      continue;
    }
    buf.push(child);
    if (buf.length === columns) flush();
  }
  flush();
  return rows;
}

/**
 * Read-only detail fields with row dividers.
 * - **Mobile (`< sm`):** always **1 column** (stacked).
 * - **`sm+`:** `columns` (1 | 2 | 3, default 2).
 */
export function DetailFieldGrid({
  children,
  className,
  columns = 2,
}: DetailFieldGridProps) {
  const items = flattenFields(Children.toArray(children));
  const rows = chunkFields(items, columns);

  return (
    <div className={cn(className)}>
      {/* Mobile: one field per row */}
      <div className="sm:hidden">
        {items.map((child, idx) => (
          <div
            key={idx}
            className="border-b border-border py-3 first:pt-0 last:border-b-0 last:pb-0"
          >
            {child}
          </div>
        ))}
      </div>

      {/* Desktop / tablet: N columns */}
      <div className="hidden sm:block">
        {rows.map((cells, idx) => {
          const full = cells.length === 1 && columns > 1;
          return (
            <div
              key={idx}
              className={cn(
                'grid min-w-0 grid-cols-1 gap-3 border-b border-border py-3 first:pt-0 last:border-b-0 last:pb-0 sm:gap-x-8',
                full ? 'sm:grid-cols-1' : DESKTOP_COLS[columns],
              )}
            >
              {cells.map((cell, cellIdx) => (
                <div key={cellIdx} className="min-w-0">
                  {cell}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}
